import React, { useState, useEffect } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import logo from '../assets/logo.png'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation } from 'react-router-dom'

const Header = () => {
  const [showDemoButton, setShowDemoButton] = useState(false)
  const [showHeaderContent, setShowHeaderContent] = useState(true)
  const [hideAllContent, setHideAllContent] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Get current location to determine which page we're on
  const location = useLocation()
  const isContactPage = location.pathname === '/contact'
  const isTeamPage = location.pathname === '/team'
  const isHomePage = location.pathname === '/' || location.pathname === ''

  // Check if we're on mobile and listen for resize events
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    // Initial check
    checkMobile()

    // Add resize listener
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY

      if (isHomePage) {
        // Home page specific logic with panels
        const panel1 = document.getElementById('panel1')
        const panel10 = document.getElementById('panel10')

        if (panel1) {
          const panel1Bottom = panel1.offsetTop + panel1.offsetHeight
          const scrollPosition = scrollY + window.innerHeight * 0.5

          // Only show the demo button after scrolling past panel1
          setShowDemoButton(scrollPosition > panel1Bottom)

          // Hide the header content (logo and team link) when scrolling down
          setShowHeaderContent(scrollY < 100)

          // Hide all header content including the demo button when reaching the last panel
          if (panel10) {
            const panel10Top = panel10.offsetTop
            const nearLastPanel =
              scrollY + window.innerHeight * 0.7 > panel10Top
            setHideAllContent(nearLastPanel)
          }
        }
      } else if (isContactPage || isTeamPage) {
        // Contact and Team pages: simple scroll behavior
        // Show demo button immediately, hide header content when scrolling down
        setShowDemoButton(true)
        setShowHeaderContent(scrollY < 100)
        setHideAllContent(false) // Never hide demo button on these pages
      }
    }

    // Add scroll listener for all pages that need scroll behavior
    if (isHomePage || isContactPage || isTeamPage) {
      window.addEventListener('scroll', handleScroll)
      // Call once to set initial state
      handleScroll()
      return () => window.removeEventListener('scroll', handleScroll)
    } else {
      // For any other pages, set default state
      setShowHeaderContent(true)
      setHideAllContent(false)
      setShowDemoButton(true)
    }
  }, [isHomePage, isContactPage, isTeamPage])

  const toggleMenu = () => {
    setMenuOpen(!menuOpen)
  }

  // Close menu when switching from mobile to desktop
  useEffect(() => {
    if (!isMobile) {
      setMenuOpen(false)
    }
  }, [isMobile])

  return (
    <div className='header'>
      <AnimatePresence>
        {showHeaderContent && !hideAllContent && (
          <motion.a
            href='/'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <LazyLoadImage
              src={logo}
              alt='Roundabout Technologies'
              className='logo'
              effect="blur"
              threshold={100}
              placeholderSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 50'%3E%3Crect width='200' height='50' fill='%23f0f0f0'/%3E%3C/svg%3E"
            />
          </motion.a>
        )}
      </AnimatePresence>

      <div style={{ flexGrow: 1 }}></div>

      {isMobile ? (
        // Mobile Navigation
        <>
          <AnimatePresence>
            {showHeaderContent && !hideAllContent && (
              <motion.div
                className='hamburger-menu'
                onClick={toggleMenu}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className={`hamburger-line ${menuOpen ? 'open' : ''}`}
                ></div>
                <div
                  className={`hamburger-line ${menuOpen ? 'open' : ''}`}
                ></div>
                <div
                  className={`hamburger-line ${menuOpen ? 'open' : ''}`}
                ></div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
            <motion.a
              href='/team'
              className='mobile-menu-item'
              onClick={() => setMenuOpen(false)}
            >
              Team
            </motion.a>
          </div>
        </>
      ) : (
        // Desktop Navigation
        <AnimatePresence>
          {showHeaderContent && !hideAllContent && (
            <motion.a
              href='/team'
              style={{ marginRight: '50px', fontSize: '21px' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              Team
            </motion.a>
          )}
        </AnimatePresence>
      )}

      <AnimatePresence>
        {/* Show demo button based on conditions and current page */}
        {((showDemoButton && !hideAllContent && !isMobile) ||
          (isTeamPage && !isMobile)) && (
          <motion.a
            className='book-demo-btn'
            href='https://calendar.app.google/SdBHwdC7pXa1HdKQ8'
            target='_blank'
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            Schedule a Demo
          </motion.a>
        )}

        {/* Show demo button on mobile for Team page */}
        {isMobile && isTeamPage && (
          <motion.a
            className='book-demo-btn mobile-demo-btn'
            href='https://calendar.app.google/SdBHwdC7pXa1HdKQ8'
            target='_blank'
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            style={{ marginTop: '20px', width: '100%' }}
          >
            Schedule a Demo
          </motion.a>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Header
